import { initializeApp } from 'firebase/app';
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager, connectFirestoreEmulator } from 'firebase/firestore';
import { getAuth, setPersistence, browserLocalPersistence, connectAuthEmulator } from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getMessaging, isSupported } from 'firebase/messaging';
import { getStorage } from 'firebase/storage';
import { getPerformance } from 'firebase/performance';

const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY            || "REPLACE_ME",
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN        || "REPLACE_ME",
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID         || "REPLACE_ME",
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET     || "REPLACE_ME",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "REPLACE_ME",
  appId:             import.meta.env.VITE_FIREBASE_APP_ID             || "REPLACE_ME",
};

const app = initializeApp(firebaseConfig);
// Auto-detect long-polling: falls back to plain HTTP long-polling on networks
// (school/stadium WiFi, restrictive firewalls) that block Firestore's default
// WebChannel/QUIC streaming transport — this is what was causing the
// "Could not reach Cloud Firestore backend" timeouts.
// persistentLocalCache: queues writes in IndexedDB so a dropped connection or
// page reload mid-write doesn't silently lose the mutation — it replays once
// the connection is restored, instead of the in-memory write just vanishing.
// cacheSizeBytes: explicit 50MB bound (not CACHE_SIZE_UNLIMITED) so Firestore's
// LRU garbage collector has an unambiguous target to evict down to. Without
// this, one production user's IndexedDB cache grew to 318MB, hit the browser's
// storage quota, and every subsequent local write started failing with
// QuotaExceededError (silently — see reportWriteFailure below, which can't
// catch this specific failure mode).
export const db = initializeFirestore(app, {
  experimentalAutoDetectLongPolling: true,
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager(), cacheSizeBytes: 50 * 1024 * 1024 }),
});
export const auth      = getAuth(app);
export const storage   = getStorage(app);
export const functions = getFunctions(app);

// Local emulator suite toggle — set VITE_USE_EMULATOR=true in .env.local (gitignored) to point
// every SDK at localhost instead of production. Must run before any other Firestore/Auth/Functions
// call in the app, which holds here since this module's top-level code finishes before any
// consumer that imported db/auth/functions gets to use them.
if (import.meta.env.VITE_USE_EMULATOR === 'true') {
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFunctionsEmulator(functions, 'localhost', 5001);
  console.warn('[firebase] Using LOCAL EMULATORS — Firestore/Auth/Functions are NOT hitting production.');
}

// Explicitly set LOCAL persistence — ensures tokens survive PWA suspension and device memory pressure
setPersistence(auth, browserLocalPersistence).catch(err => {
  console.warn('[Auth] Could not set persistence:', err);
});

// Performance Monitoring — prod only to avoid noise from local dev
export const perf = import.meta.env.PROD ? getPerformance(app) : null;

// Messaging: only available in browsers that support it (not in SW context or old browsers)
let _messaging = null;
export const getAppMessaging = async () => {
  if (_messaging) return _messaging;
  try {
    const supported = await isSupported();
    if (supported) _messaging = getMessaging(app);
  } catch {}
  return _messaging;
};
